/**
 * BrioBase -- shared foundation for brio custom elements.
 *
 * Extends HTMLElement, the browser's base class for custom elements.
 * Components that extend BrioBase register with the browser via
 * customElements.define() and automatically receive lifecycle hooks:
 *
 *   connectedCallback()    -- called when the element is added to the DOM.
 *                             Use this to set up listeners and initial state.
 *   disconnectedCallback() -- called when the element is removed from the DOM.
 *                             Use this to clean up listeners and references.
 *
 * These lifecycle hooks are defined by each component, not here in the base.
 * The base provides shared utilities that multiple components need:
 *
 *   $(selector)                -- scoped querySelector
 *   $$(selector)               -- scoped querySelectorAll (returns Array)
 *   emit(name, detail)         -- dispatch a namespaced CustomEvent
 *   keyNav(selector, options)  -- roving tabindex keyboard navigation
 */
export class BrioBase extends HTMLElement {
    /**
     * Scoped query -- finds the first matching descendant.
     * Equivalent to this.querySelector(sel), just shorter.
     */
    $(sel) {
        return this.querySelector(sel);
    }

    /**
     * Scoped query all -- finds all matching descendants as an Array.
     * querySelectorAll returns a NodeList (not a real Array), so we convert
     * it here to get access to indexOf, map, filter, etc.
     */
    $$(sel) {
        return Array.from(this.querySelectorAll(sel));
    }

    /**
     * Dispatch a namespaced custom event.
     *
     * Events are prefixed with "brio:" to avoid collisions with native
     * events or other libraries. Setting bubbles: true lets parent elements
     * listen for events from their children without direct references.
     *
     * Example: this.emit('tab-change', { tab }) dispatches an event
     * named "brio:tab-change" that any ancestor can listen for.
     */
    emit(name, detail) {
        this.dispatchEvent(new CustomEvent(`brio:${name}`, { bubbles: true, detail }));
    }

    /**
     * Attach roving tabindex keyboard navigation to items matching selector.
     *
     * "Roving tabindex" is the ARIA pattern where only the active item in a
     * group is tabbable (tabindex=0). Arrow keys move focus between items,
     * and the focused item becomes the new tab stop. This lets users Tab
     * into the group, arrow through it, and Tab out -- matching native
     * behavior of <select>, radio groups, etc.
     *
     * The handler queries items fresh on each keydown (not cached) so it
     * works correctly when items are added or removed dynamically.
     *
     * Options:
     *   vertical -- use ArrowUp/ArrowDown instead of Left/Right (default: false)
     *   wrap     -- wrap around at ends (default: true)
     *   onFocus  -- callback(item) invoked when an item receives focus,
     *               useful for activating the focused item (e.g. showing a tab panel)
     *
     * Returns a cleanup function that removes the keydown listener.
     * Call it in disconnectedCallback to prevent leaks.
     */
    keyNav(selector, opts = {}) {
        const vertical = opts.vertical || false;
        const wrap = opts.wrap !== false;
        const onFocus = opts.onFocus || null;
        const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';
        const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';

        const handler = (e) => {
            // Query fresh each time so dynamic item changes are picked up
            const items = this.$$(selector);
            if (!items.length) return;

            const idx = items.indexOf(e.target);
            if (idx === -1) return; // keydown came from outside our item set

            let next = -1;
            if (e.key === nextKey)
                next = wrap ? (idx + 1) % items.length : Math.min(idx + 1, items.length - 1);
            else if (e.key === prevKey)
                next = wrap ? (idx - 1 + items.length) % items.length : Math.max(idx - 1, 0);
            else if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = items.length - 1;
            else return; // not a nav key, let it propagate normally

            e.preventDefault();
            items[next].focus();
            if (onFocus) onFocus(items[next]);
        };

        this.addEventListener('keydown', handler);
        return () => this.removeEventListener('keydown', handler);
    }
}
